import { useEffect, useState } from 'react';
import Dashboard from '@uppy/react/dashboard';

import '@uppy/core/css/style.css';
import '@uppy/dashboard/css/style.css';
import '@uppy/audio/css/style.css';
import '@uppy/screen-capture/css/style.css';
import '@uppy/webcam/css/style.css';
import Uppy from '@uppy/core';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';

type Props = {
  setEnableFiles?: (enable: boolean) => void;
  width?: number | string;
  height?: number;
  note?: string;
  setUploading?: (start: boolean) => void;
  uppy: Uppy;
  target?: string;
  plugins?: string[];
  onRecordingStateChange?: (isRecording: boolean) => void;
};

export const UppyDashboard = ({
  setEnableFiles,
  width = 350,
  height = 300,
  note,
  setUploading,
  uppy,
  target,
  plugins,
  onRecordingStateChange
}: Props) => {
  const { t } = useTranslation();
  const { theme } = useThemeContext();
  const [filesChanged, setFilesChanged] = useState<boolean>(false);
  uppy.on('file-removed', () => {
    setFilesChanged(true);
    setUploading?.(false);
  });

  useEffect(() => {
    const items = uppy.getFiles();
    if (items.length > 0) {
      setEnableFiles?.(true);
    } else {
      setEnableFiles?.(false);
    }

    if (filesChanged) setFilesChanged(false);
  }, [filesChanged, setEnableFiles]);

  // Monitor recording state by checking for recording UI elements in the DOM
  useEffect(() => {
    if (!onRecordingStateChange) return;

    const checkInterval = setInterval(() => {
      // Check if any recording plugin is active by looking for recording length timer
      // The recording length element only appears when actively recording (shows elapsed time)
      const webcamRecordingLength = document.querySelector(
        '.uppy-Webcam-recordingLength'
      );
      const audioRecordingLength = document.querySelector(
        '.uppy-Audio-recordingLength'
      );
      const screenRecordingLength = document.querySelector(
        '.uppy-ScreenCapture-recordingLength'
      );

      const isRecording = !!(
        webcamRecordingLength ||
        audioRecordingLength ||
        screenRecordingLength
      );

      onRecordingStateChange(isRecording);
    }, 300); // Check every 300ms

    return () => clearInterval(checkInterval);
  }, [onRecordingStateChange]);

  const supportsScreenCapture =
    typeof navigator.mediaDevices?.getDisplayMedia === 'function';

  return (
    <Dashboard
      uppy={uppy}
      width={width}
      height={height}
      target={target || '#root'}
      plugins={
        plugins ||
        [
          'Webcam',
          'Audio',
          supportsScreenCapture ? 'ScreenCapture' : null
        ].filter((p): p is string => p !== null)
      }
      proudlyDisplayPoweredByUppy={false}
      hideProgressDetails={false}
      hideUploadButton={true}
      hidePauseResumeButton={true}
      hideCancelButton={true}
      hideRetryButton={false}
      disableInformer={true}
      theme={theme === 'dark' ? 'dark' : 'light'}
      locale={{
        strings: {
          dropPasteImportFiles: note ? note : t('drop-your-files-here')
        }
      }}
    />
  );
};
